import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { newUuid } from '@shared';
import { uploadFileToS3 } from '@client/util/uploadFileToBucket';
import {
  rateActionApi,
  rateActionKeys,
  type PostRateActionExtractBody,
  type RateActionExtractResponse,
} from '../api/rate-action-api';

export type RateActionUploadStatus =
  | 'idle'
  | 'requestingUrl'
  | 'uploadingFile'
  | 'enqueueing'
  | 'polling'
  | 'completed'
  | 'error';

export interface UseRateActionExtractParams {
  clientId: string;
  letterTemplate: string;
}

export interface UploadAndExtractInput {
  file: File;
}

const POLL_INTERVAL_MS = 5000;

export function useRateActionExtract(params: UseRateActionExtractParams) {
  const { clientId, letterTemplate } = params;
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RateActionUploadStatus>('idle');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const uploadAndExtract = useMutation({
    mutationFn: async (input: UploadAndExtractInput) => {
      const newRequestId = newUuid();
      setRequestId(newRequestId);
      setErrorMessage(null);

      setStatus('requestingUrl');
      const presigned = await rateActionApi.getPresignedUrl(clientId, input.file.name);

      setStatus('uploadingFile');
      await uploadFileToS3(presigned.url, input.file, 'application/pdf');

      setStatus('enqueueing');
      const body: PostRateActionExtractBody = {
        requestId: newRequestId,
        clientId,
        letterTemplate,
        fileBucket: presigned.bucket,
        fileKey: presigned.key,
      };
      await rateActionApi.startExtract(body);

      // Seed the detail-query cache so the parent's useRateActionExtractRequest reads
      // status='processing' immediately on mount — without this, there's a flash of the
      // generic "Loading letter…" spinner between the URL change and the first poll.
      queryClient.setQueryData<RateActionExtractResponse>(
        rateActionKeys.detail(newRequestId),
        { requestId: newRequestId, status: 'processing', letterTemplate },
      );

      setStatus('polling');
      return newRequestId;
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setErrorMessage(msg);
      setStatus('error');
    },
  });

  const pollQuery = useQuery<RateActionExtractResponse>({
    queryKey: rateActionKeys.detail(requestId ?? ''),
    queryFn: () => rateActionApi.getExtractedData(requestId!),
    // Stay enabled once we have a requestId so on-demand refetches (e.g., after the
    // "Apply this option" mutation invalidates) actually fire. Polling cadence is
    // controlled separately via refetchInterval below.
    enabled: !!requestId,
    refetchInterval: status === 'polling' ? POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    const data = pollQuery.data;
    if (!data || status !== 'polling') return;
    if (data.status === 'completed' || data.status === 'applied') {
      setStatus('completed');
    } else if (data.status === 'error') {
      setErrorMessage(data.error ?? 'Extraction failed');
      setStatus('error');
    }
  }, [pollQuery.data, status]);

  function reset() {
    setStatus('idle');
    setRequestId(null);
    setErrorMessage(null);
    uploadAndExtract.reset();
  }

  return {
    status,
    requestId,
    errorMessage,
    extractData: pollQuery.data,
    startUpload: (file: File) => uploadAndExtract.mutate({ file }),
    reset,
  };
}
